import { Node } from "reactflow"
import { GraphNodeData, GraphNodeEdgeData, GroupNodeData } from "@store/services/converter/types"
import { SyntheticEvent } from "react"
import { GraphNodeType } from "@custom-types/graphs"
import { AppNode } from "@features/builder/graph/types"

export interface GraphDiffData {
  n1?: Node<GraphNodeData | GroupNodeData>
  n2?: Node<GraphNodeData | GroupNodeData>
  type: GraphDiffType
}

export enum GraphDiffType {
  NodeType,
  GroupLabel,
  NodeLabel,
  Expression,
  NodeAdded,
  NodeMissing,
}

export const graphNodeIdFromEvent = (
  e: SyntheticEvent<Element, MouseEvent>,
): string | undefined => {
  let parent: Element | null = e.target as Element
  while (parent) {
    const id = parent.getAttribute("data-id")
    if (id) return id
    parent = parent.parentElement
  }
}

export const calculateGraphDiff = (
  graph1: GraphNodeEdgeData,
  graph2: GraphNodeEdgeData,
): GraphDiffData[] => {
  const diff: GraphDiffData[] = []
  graph1.nodes.forEach((n1) => {
    const n2 = graph2.nodes.filter((n2) => n1.id === n2.id)[0]
    if (!n2) {
      diff.push({ n1, n2, type: GraphDiffType.NodeAdded })
    } else {
      const comp = graphCompareNode(n1, n2)
      if (comp) diff.push(comp)
    }
  })
  graph2.nodes.forEach((n2) => {
    const n1 = graph1.nodes.filter((n1) => n1.id === n2.id)[0]
    if (!n1) diff.push({ n1, n2, type: GraphDiffType.NodeMissing })
  })
  return diff
}

export const graphCompareNode = (
  n1: Node<GraphNodeData | GroupNodeData>,
  n2: Node<GraphNodeData | GroupNodeData>,
): GraphDiffData | undefined => {
  if ("group" in n1.data || "group" in n2.data) {
    if ("group" in n1.data && "group" in n2.data) {
      if (n1.data.group.label === n2.data.group.label) return
      return { n1, n2, type: GraphDiffType.GroupLabel }
    }
    return { n1, n2, type: GraphDiffType.NodeType }
  }
  if (n1.data.node?.type !== n2.data.node?.type) return { n1, n2, type: GraphDiffType.NodeType }
  if (n1.data.display.label !== n2.data.display.label)
    return { n1, n2, type: GraphDiffType.NodeLabel }
  // deterministic
  if (n1.data.node?.type === "deterministic" && n2.data.node?.type === "deterministic") {
    if (n1.data.expression !== n2.data.expression) return { n1, n2, type: GraphDiffType.Expression }
  }
  return undefined
}

export const getNodeTypeMap = (
  nodesMap: Record<string, GraphNodeData>,
): Record<string, GraphNodeType> => {
  return Object.entries(nodesMap).reduce((map: Record<string, GraphNodeType>, [id, node]) => {
    map[id] = node.type

    return map
  }, {})
}

/**
 * Takes list of nodes and list of node IDs
 * Returns sorted list of objects containing node label and ID
 */
export function getSortedNodeLabelsById(nodes: AppNode[], nodeIds: string[]) {
  const labels: { label: string; id: string }[] = []
  for (const parentId of nodeIds) {
    const node = nodes.find((n) => n.id === parentId)
    const label = node?.data.label ?? ""
    if (label) {
      labels.push({ label, id: parentId })
    }
  }
  return labels.sort((a, b) => a.label.localeCompare(b.label))
}
